import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { getLeads, saveLeads, getSettings } from "./demoConfig";
import DemoScreens2 from "./DemoScreens2";
import CloserSidebar2 from "./CloserSidebar2";
import WindowPortal from "./WindowPortal";
import { Monitor, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import "./demo2.css";

const TOTAL_SCREENS = 20;
const API = process.env.REACT_APP_BACKEND_URL || "";

const SCREEN_TITLES = [
  "Welcome",
  "About You",
  "Referrals 101",
  "Reveal + Flow",
  "Watch It Live",
  "Imagine 30 Days",
  "$99 Pitch",
  "Permission Upsell",
  "Meet BJA",
  "3 Channels",
  "Live Proof",
  "Apps Math",
  "Case Studies",
  "Two Roads",
  "Two Plans",
  "Almost Said No",
  "Mind Reader",
  "After You Pay",
  "Close & Pay",
  "Thank You",
];

const CLOSER_TITLES = [
  "Greet + Promise",
  "Qualify",
  "Educate Referrals",
  "Reveal + Flow",
  "LIVE Demo CR",
  "Future-Pace 30 Days",
  "Close $99",
  "Permission Upsell",
  "Meet BJA",
  "3 Channels Flow",
  "Show Live Proof",
  "Personalized Math",
  "Proof Wall",
  "Two Roads",
  "Stack the Value",
  "Crush Skeptic Doubt",
  "Read Their Mind",
  "Show First Hour",
  "Take Payment",
  "Wrap + PDF",
];

export default function LiveDemo2() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { auth } = useOutletContext();
  const [lead, setLead] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(1);
  const [showShareModal, setShowShareModal] = useState(true);

  // Inputs
  const [weeklyApps, setWeeklyApps] = useState("");
  const [confirmedRole, setConfirmedRole] = useState("");
  const [confirmedMarket, setConfirmedMarket] = useState("");
  const [linkedInConnections, setLinkedInConnections] = useState("");
  const [linkedInReferrals, setLinkedInReferrals] = useState("");
  const [monthsSearching, setMonthsSearching] = useState("");

  // Slide 13 — gmail mode for live proof
  const [gmailMode, setGmailMode] = useState(false);

  // Slide 15 — case studies + monthly results PDF
  const [selectedCaseStudies, setSelectedCaseStudies] = useState([]);
  const [proofTab, setProofTab] = useState("pain");
  const [pdfMonth, setPdfMonth] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState(null);

  // Slide 17 — close
  const [selectedClose, setSelectedClose] = useState(null);

  // Popout
  const [popoutMode, setPopoutMode] = useState(false);
  const [popoutReady, setPopoutReady] = useState(false);

  // Timer
  const [elapsed, setElapsed] = useState(0);
  const startTime = useRef(Date.now());
  const demoStartedAtRef = useRef(new Date().toISOString());

  useEffect(() => {
    const leads = getLeads();
    const found = leads.find((l) => l.id === leadId);
    if (!found) { navigate("/demo/crm"); return; }
    setLead(found);
    setWeeklyApps(found.weeklyApps || "");
    setConfirmedRole(found.targetRole || "");
    setConfirmedMarket(found.targetMarket || "");
    setMonthsSearching(found.monthsSearching || "");
    setLinkedInConnections(found.linkedInConnections || "");
    setLinkedInReferrals(found.linkedInReferrals || "");
  }, [leadId, navigate]);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startTime.current) / 1000)), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = () => setPopoutReady(true);
    window.addEventListener("popout-ready", handler);
    return () => window.removeEventListener("popout-ready", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateLeadField = async (updates) => {
    const leads = getLeads();
    const updated = leads.map((l) => (l.id === leadId ? { ...l, ...updates } : l));
    saveLeads(updated);
    setLead((prev) => ({ ...prev, ...updates }));
    try {
      await fetch(`${API}/api/demo/leads/${leadId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
    } catch (e) { console.error("sync fail:", e); }
  };

  const closePlanMap = {
    cr:     { plan: "chillrefer-99", label: "ChillRefer only", amount: 99,    pay: "monthly" },
    h90_a:  { plan: "hired-in-90",   label: "Hired in 90 — Standard split",  amount: 1499, pay: "split-2"     },
    h90_c:  { plan: "hired-in-90",   label: "Hired in 90 — Pay in full",     amount: 1299, pay: "full"        },
  };

  const toggleCaseStudy = (id) => {
    setSelectedCaseStudies((p) => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const endDemo = () => {
    const choice = selectedClose ? closePlanMap[selectedClose] : null;
    updateLeadField({
      status: "demo_done",
      demoDoneAt: new Date().toISOString(),
      demoType: "demo2",
      weeklyApps: parseFloat(weeklyApps) || null,
      monthsSearching: parseFloat(monthsSearching) || null,
      linkedInConnections: parseFloat(linkedInConnections) || null,
      linkedInReferrals: parseFloat(linkedInReferrals) || null,
      recommendedPlan: choice ? choice.plan : null,
      selectedPlan: choice ? choice.plan : null,
      demo2CloseChoice: selectedClose || null,
      demo2PaymentOption: choice ? choice.pay : null,
      demo2Amount: choice ? choice.amount : null,
      painPoints: selectedCaseStudies,
    });
    setPopoutMode(false);
    navigate("/demo/crm");
  };

  const formatTimer = () => {
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handlePopoutClose = useCallback(() => {
    setPopoutMode(false);
    setPopoutReady(false);
  }, []);

  if (!lead) return null;

  const settings = getSettings();

  const sharedState = {
    lead, currentScreen,
    weeklyApps, confirmedRole, confirmedMarket,
    linkedInConnections, linkedInReferrals, monthsSearching,
    gmailMode,
    selectedCaseStudies, proofTab, pdfMonth, pdfPage, pdfTotalPages,
    selectedClose,
    settings, auth,
    demoStartedAt: demoStartedAtRef.current,
  };

  const sharedActions = {
    setCurrentScreen,
    setWeeklyApps, setConfirmedRole, setConfirmedMarket,
    setLinkedInConnections, setLinkedInReferrals, setMonthsSearching,
    setGmailMode,
    toggleCaseStudy, setProofTab, setPdfMonth, setPdfPage, setPdfTotalPages,
    setSelectedCaseStudies,
    setSelectedClose,
    updateLeadField, endDemo,
  };

  const sidebarProps = {
    state: sharedState,
    actions: sharedActions,
    elapsed: formatTimer(),
    screenTitles: SCREEN_TITLES,
    closerTitles: CLOSER_TITLES,
    popoutMode,
    onPopout: () => setPopoutMode(true),
    totalScreens: TOTAL_SCREENS,
  };

  return (
    <div className="live-demo-container" data-testid="live-demo2-container">
      {showShareModal && (
        <div className="share-modal-overlay" data-testid="share-modal-d2">
          <div className="share-modal-card">
            <div className="share-modal-icon-wrap"><Monitor className="share-modal-icon" /></div>
            <h2>Before you share your screen</h2>
            <p>Share <strong>only the left panel</strong> with the client. Use <strong>Pop Out</strong> in the sidebar to detach your controls.</p>
            <div className="share-modal-steps">
              <div className="share-step"><span className="share-step-num">1</span>Click <strong>"Pop Out"</strong></div>
              <div className="share-step"><span className="share-step-num">2</span>Share this tab via Zoom/Meet</div>
              <div className="share-step"><span className="share-step-num">3</span>20 slides — ChillRefer first, BJA upsell second</div>
            </div>
            <Button onClick={() => setShowShareModal(false)} className="share-modal-btn" data-testid="dismiss-share-modal-d2">
              Got it, start demo
            </Button>
          </div>
        </div>
      )}

      <div className={`live-demo-split ${popoutMode ? "live-demo-fullwidth" : ""}`}>
        <div className="live-demo-left" data-testid="demo2-left-panel">
          <div className="live-demo-left-header">
            <span className="live-screen-num">{currentScreen}/{TOTAL_SCREENS}</span>
            <span className="live-screen-label">{SCREEN_TITLES[currentScreen - 1]}</span>
            <span className="live-screen-label" style={{ marginLeft: 8, color: "#7F77DD", fontWeight: 700 }}>· Demo 2</span>
            {popoutMode && (
              <span className="popout-indicator">
                <ExternalLink className="w-3.5 h-3.5" /> Controls in popup
              </span>
            )}
          </div>
          <div className="live-demo-left-content">
            <DemoScreens2 state={sharedState} actions={sharedActions} />
          </div>
          <div className="live-demo-left-footer">
            <Button variant="outline" size="sm" disabled={currentScreen <= 1} onClick={() => setCurrentScreen((s) => s - 1)} data-testid="prev-screen-btn-d2" className="live-nav-btn">
              Previous
            </Button>
            <span className="live-footer-dots">
              {SCREEN_TITLES.map((_, i) => (
                <span key={`dot-${i}`} className={`live-dot ${i + 1 === currentScreen ? "active" : ""} ${i + 1 < currentScreen ? "done" : ""}`} />
              ))}
            </span>
            <Button size="sm" className="live-next-btn" disabled={currentScreen >= TOTAL_SCREENS} onClick={() => setCurrentScreen((s) => s + 1)} data-testid="next-screen-btn-d2">
              Next
            </Button>
          </div>
        </div>

        {!popoutMode && (
          <div className="live-demo-right" data-testid="demo2-right-panel">
            <CloserSidebar2 {...sidebarProps} />
          </div>
        )}

        {popoutMode && (
          <WindowPortal onClose={handlePopoutClose} title="Closer Controls — Demo 2 (ChillRefer)">
            <div style={{ height: "100vh", overflow: "auto", background: "#fafafa" }}>
              <CloserSidebar2 {...sidebarProps} />
            </div>
          </WindowPortal>
        )}
      </div>
    </div>
  );
}
